import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { Calendar, ChevronLeft, ChevronRight, Clock, User, MapPin } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

function ClientDayCalendar({ db, client }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const { theme } = useTheme();

  const fetchEmployees = useCallback(async () => {
    if (!db) return;
    try {
      const employeesRef = collection(db, 'employees');
      // Récupérer tous les employés sans filtre isArchived côté Firestore
      const querySnapshot = await getDocs(employeesRef);
      const fetchedEmployees = querySnapshot.docs.map(doc => ({
        userId: doc.id,
        ...doc.data(),
        color: doc.data().color || '#808080'
      }));
      
      // Filtrer côté client pour inclure les employés où isArchived est false ou absent
      const filteredEmployees = fetchedEmployees.filter(employee => {
        // Exclure les comptes bénéficiaires (rôle 'beneficiaire')
        if (employee.role === 'beneficiaire') {
          return false;
        }
        
        // Inclure si isArchived est false ou absent/undefined
        const isNotArchived = employee.isArchived !== true;
        const isActive = employee.isActive !== false;
        
        return isNotArchived && isActive;
      });
      
      console.log('🔍 Employés récupérés dans ClientDayCalendar:', {
        total: fetchedEmployees.length,
        filtrés: filteredEmployees.length,
        détails: fetchedEmployees.map(emp => ({
          id: emp.userId,
          nom: emp.name || `${emp.firstName} ${emp.lastName}`,
          isArchived: emp.isArchived,
          isActive: emp.isActive,
          role: emp.role
        }))
      });
      
      setEmployees(filteredEmployees);
    } catch (error) {
      console.error("Erreur lors de la récupération des employés:", error);
    }
  }, [db]);

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(emp => emp.userId === employeeId);
    if (employee) {
      return employee.name || `${employee.firstName} ${employee.lastName}`;
    } else {
      console.warn(`Employé non trouvé pour l'ID: ${employeeId}`);
      return "Employé non trouvé";
    }
  };

  const getEmployeeColor = (employeeId) => {
    const employee = employees.find(emp => emp.userId === employeeId);
    return employee?.color || '#808080'; // Couleur par défaut si non trouvée
  };

  useEffect(() => {
    fetchEmployees();
    loadAppointments();
  }, [client, currentDate, fetchEmployees]);

  const loadAppointments = async () => {
    try {
      const startOfDay = new Date(currentDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(currentDate);
      endOfDay.setHours(23, 59, 59, 999);

      const appointmentsRef = collection(db, 'appointments');
      const q = query(
        appointmentsRef, 
        where('clientId', '==', client.id),
        where('date', '>=', startOfDay),
        where('date', '<=', endOfDay)
      );
      
      const querySnapshot = await getDocs(q);
      const appointmentsData = [];
      
      querySnapshot.forEach((doc) => {
        const appointment = {
          id: doc.id,
          ...doc.data()
        };
        appointmentsData.push(appointment);
      });

      // Trier par heure de début
      appointmentsData.sort((a, b) => a.startTime.localeCompare(b.startTime));
      setAppointments(appointmentsData);
    } catch (error) {
      console.error('Erreur lors du chargement des rendez-vous:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigateDay = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + direction);
    setCurrentDate(newDate);
  };

  const formatTime = (time) => {
    return time.substring(0, 5); // Format HH:MM
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Chargement du planning...</p>
        </div>
      </div>
    );
  }

  const dayName = formatDate(currentDate);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
          <Calendar className="mr-3 text-blue-600 dark:text-blue-400" size={24} />
          Planning du jour
        </h2>
        
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigateDay(-1)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-300"
            >
              <ChevronLeft size={20} />
            </button>
            
            <span className="text-lg font-medium text-gray-900 dark:text-white">
              {dayName}
            </span>
            
            <button
              onClick={() => navigateDay(1)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-300"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="space-y-4">
        {appointments.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 dark:text-gray-500 mb-4">
              <Calendar size={48} className="mx-auto" />
            </div>
            <p className="text-gray-500 dark:text-gray-400 text-lg">Aucun rendez-vous prévu aujourd'hui</p>
          </div>
        ) : (
          appointments.map((appointment) => {
            const employeeColor = appointment.employeeId ? getEmployeeColor(appointment.employeeId) : '#808080';
            return (
              <div
                key={appointment.id}
                className="bg-white dark:bg-gray-700 rounded-xl p-6 border-2 hover:shadow-lg transition-all duration-300"
                style={{
                  borderLeftColor: employeeColor,
                  borderLeftWidth: '4px',
                  '--employee-color': employeeColor
                }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    {/* Icône colorée de l'employé */}
                    <div
                      className="p-3 rounded-xl shadow-sm"
                      style={{ backgroundColor: employeeColor }}
                    >
                      <User className="text-white" size={20} />
                    </div>

                    {/* Contenu principal */}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center">
                            <Clock
                              className="mr-2"
                              style={{ color: employeeColor }}
                              size={18}
                            />
                            <span className="font-semibold text-gray-900 dark:text-white">
                              {formatTime(appointment.startTime)} - {formatTime(appointment.endTime)}
                            </span>
                          </div>
                          <div className="h-1 w-1 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                          <span className="text-gray-700 dark:text-gray-300 font-medium">
                            {appointment.employeeId ? getEmployeeName(appointment.employeeId) : 'Intervenant non spécifié'}
                          </span>
                        </div>
                      </div>

                      {/* Service */}
                      <div className="flex items-center text-gray-600 dark:text-gray-400">
                        <MapPin className="mr-2" size={16} />
                        <span className="font-medium">{appointment.serviceType}</span>
                      </div>

                      {/* Statut */}
                      <div className="mt-3">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                          appointment.status === 'confirmed'
                            ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                            : appointment.status === 'cancelled'
                            ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                            : 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                        }`}>
                          {appointment.status === 'confirmed' ? 'Confirmé' :
                           appointment.status === 'cancelled' ? 'Annulé' : 'En attente'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Stats du jour */}
      {appointments.length > 0 && (
        <div className="mt-8 pt-6 border-t dark:border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{appointments.length}</p>
              <p className="text-gray-600 dark:text-gray-400">Rendez-vous total</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {appointments.filter(a => a.status === 'confirmed').length}
              </p>
              <p className="text-gray-600 dark:text-gray-400">Confirmés</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {new Set(appointments.map(a => a.employeeId)).size}
              </p>
              <p className="text-gray-600 dark:text-gray-400">Intervenants différents</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientDayCalendar;
